/**
 * Returns the numeric metric value from the flat VDS all-metrics response object.
 *
 * @param {Object|null} data - Flat VDS response object.
 * @param {string} fieldName - VDS field alias.
 * @returns {number|null}
 */
export function getMetricValue(data, fieldName) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const val = data[fieldName];
  if (val === null || val === undefined) return null;
  const num = Number(val);
  return Number.isFinite(num) ? num : null;
}

/**
 * Extracts the value for the given key
 * when workBookName is 'Total Claims Trend' and showMetric is true.
 * @param {Array} metricsData - The metrics data array from API.
 * @param {string} workBookName - The workbook name.
 * @param {boolean} showMetric - Whether to show the metric.
 * @param {string} key - The key to extract.
 * @returns {string|null} - The extracted value or null if not found.
 */
export function extractMetricValue(metricsData, workBookName, showMetric, key) {
  if (showMetric && workBookName === 'Total Claims Trend' && Array.isArray(metricsData) && metricsData.length > 0) {
    return metricsData[0][key] ?? null;
  } else if (showMetric && workBookName === 'Rx Claims Trend' && Array.isArray(metricsData) && metricsData.length > 0) {
    return metricsData[0][key] ?? null;
  } else if (showMetric && workBookName === 'Med Claims Trend' && Array.isArray(metricsData) && metricsData.length > 0) {
    return metricsData[0][key] ?? null;
  } else if (showMetric && workBookName === 'Current Active Members' && Array.isArray(metricsData) && metricsData.length > 0) {
    return metricsData[0][key] ?? null;
  } else if (showMetric && workBookName === 'Gross Cost by Month' && Array.isArray(metricsData) && metricsData.length > 0) {
    return metricsData[0][key] ?? null;
  }
  return null;
}

/**
 * Returns the sheet name for a given workbook name.
 * If workBookName is 'Total Claims Trend', returns 'Total Trend'.
 * Otherwise returns null.
 * @param {string} workBookName
 * @returns {string|null}
 */
export const getMetricCardLabelByWorkBookName = (workBookName) => {
  if (workBookName === 'Total Claims Trend') {
    return 'Total Claims Trend';
  } else if (workBookName === 'Med Claims Trend') {
    return 'Medical Claims Trend';
  } else if (workBookName === 'Rx Claims Trend') {
    return 'Rx Claims Trend';
  } else if (workBookName === 'Surplus/Deficit') {
    return 'Surplus/Deficit';
  } else if (workBookName === 'Current Active Members') {
    return 'Current Enrollment';
  } else if (workBookName === 'Metrics v2') {
    return 'Gross Claims Cost';
  } else if (workBookName === 'Benchmark - High Cost Claimants') {
    return 'High Cost Claimants per 1,000 Threshold: $100K';
  } else if (workBookName === 'Member Cost Share Trend') {
    return 'Member Cost Share By Plan Year';
  } else {
    return 'Invalid WorkBook Name';
  }
};


/**
 * Returns the (parsed) Measure Value for a given Measure Name.
 * @param {Array<{ "Measure Values": string, "Measure Names": string }>} payload
 * @param {string} measureName
 * @returns {number|null}
 */
export function getMeasureValueByName(payload, measureName) {
  if (!Array.isArray(payload)) return null;
  const entry = payload.find(e => e["Measure Names"] === measureName);
  if (!entry) return null;
  const raw = entry["Measure Values"];
  const num = Number(raw);
  return Number.isFinite(num) ? num : null;
}

/**
 * Builds a lookup object: { measureName: numberValue }
 * @param {Array<{ "Measure Values": string, "Measure Names": string }>} payload
 * @returns {Record<string, number>}
 */
export function buildMeasureLookup(payload) {
  if (!Array.isArray(payload)) return {};
  const out = {};
  for (const e of payload) {
    const name = e["Measure Names"];
    const val = Number(e["Measure Values"]);
    if (name && Number.isFinite(val)) {
      out[name] = val;
    }
  }
  return out;
}


/**
 * Maps a metricType (measure name) to its corresponding workbook name.
 * - Returns "Total Claims Trend" for Total Claims measures
 * - Returns "Med Claims Trend" for Medical Claims measures
 * - Returns "Rx Claims Trend" for Pharmacy Claims measures
 * - Returns null if not matched
 * @param {string} metricType
 * @returns {string|null}
 */
export function getMetricCardLabelByMetricType(metricType) {
  if (!metricType) return null;

  const TOTAL = new Set([
    "Metric - Total Claims PEPM % Diff",
    "Metric - Total Claims PMPM % Diff",
  ]);

  const MED = new Set([
    "Metric - Med Claims PEPM % Diff",
    "Metric - Med Claims PMPM % Diff",
  ]);

  const RX = new Set([
    "Metric - Rx Claims PEPM % Diff",
    "Metric - Rx Claims PMPM % Diff",
  ]);

  if (TOTAL.has(metricType)) return "Total Claims Trend";
  if (MED.has(metricType)) return "Med Claims Trend";
  if (RX.has(metricType)) return "Rx Claims Trend";
  return null;
}

/**
 * Returns the specific Claims measure name based on workbook, metricType and toggle.
 * @param {string} workBookName
 * @param {'PMPM'|'PEPM'|null} metricType
 * @param {'Current'|'Prior'|null} toggle
 * @returns {string|null}
 */
export function getMeasureName(workBookName, metricType, toggle) {
  if (metricType === 'PEPM' && workBookName === 'Total Claims Trend') return 'Metric - Total Claims PEPM % Diff';
  else if (metricType === 'PMPM' && workBookName === 'Total Claims Trend') return 'Metric - Total Claims PMPM % Diff';
  else if (metricType === 'PEPM' && workBookName === 'Med Claims Trend') return 'Metric - Med Claims PEPM % Diff';
  else if (metricType === 'PMPM' && workBookName === 'Med Claims Trend') return 'Metric - Med Claims PMPM % Diff';
  else if (metricType === 'PEPM' && workBookName === 'Rx Claims Trend') return 'Metric - Rx Claims PEPM % Diff';
  else if (metricType === 'PMPM' && workBookName === 'Rx Claims Trend') return 'Metric - Rx Claims PMPM % Diff';
  else if (metricType === 'PMPM' && workBookName === 'Metrics v2') return 'Gross Cost PMPM Diff';
  else if (metricType === 'PEPM' && workBookName === 'Metrics v2') return 'Gross Cost PEPM Diff';
  else if (toggle === 'Current' && workBookName === 'Metrics v2') return 'Member Cost Share Curr';
  else if (toggle === 'Prior' && workBookName === 'Metrics v2') return 'Member Cost Share Prior';
  else if (!metricType && workBookName === 'Metrics v2') return '# HCC per 1000 Current';
  else return null;
}